import { T } from 'consts/translationKeys'
import eventBus from 'services/event-bus/eventBus'
import { EVENT_TYPES } from 'services/event-bus/eventTypes'
import {
  getLoadingProgressFromLocalStorage,
  ILoadingProgressData,
  removeLoadingProgressFromLocalStorage,
  updateLoadingProgressDataInLocalStorage
} from 'utils/localStorage'

import { translate } from 'utils/translations'

interface IMessageRange {
  startThreshold: number
  endThreshold: number
  text: string
}

interface IProgressPhase {
  maxProgress: number
  baseStep: number
  baseDelay: number
  speedMultiplier: number
}

const TICK_INTERVAL_MS = 100
const MIN_UPDATE_DELAY_MS = 50
const TARGET_UPDATES_COUNT = 200
const STALL_PHASE_UPDATE_INTERVAL_MS = 3000
const COMPLETION_DELAY_MS = 1500
const STALL_THRESHOLD = 99
export const LOADING_FINAL_MANDATORY_VALUE = 100

const MESSAGE_RANGES: IMessageRange[] = [
  {
    startThreshold: 0,
    endThreshold: 10,
    text: translate(T.PREPARING_THE_VIDEO)
  },
  {
    startThreshold: 10,
    endThreshold: 20,
    text: translate(T.SETTING_UP_THE_SCENES)
  },
  {
    startThreshold: 20,
    endThreshold: 30,
    text: translate(T.PLANNING_THE_MOTION)
  },
  {
    startThreshold: 30,
    endThreshold: 40,
    text: translate(T.CREATING_THE_VIDEO)
  },
  {
    startThreshold: 40,
    endThreshold: 50,
    text: translate(T.RENDERING_FRAMES)
  },
  {
    startThreshold: 50,
    endThreshold: 60,
    text: translate(T.ADDING_MOVEMENT)
  },
  {
    startThreshold: 60,
    endThreshold: 70,
    text: translate(T.PUTTING_IT_TOGETHER)
  },
  { startThreshold: 70, endThreshold: 80, text: translate(T.FINAL_FRAMES) },
  { startThreshold: 80, endThreshold: 90, text: translate(T.ALMOST_READY) },
  {
    startThreshold: 90,
    endThreshold: 98,
    text: translate(T.FINISHING_THE_VIDEO)
  }
]

const STALL_MESSAGES = [
  translate(T.DRAMATIC_PAUSE),
  translate(T.SO_CLOSE_YOU_CAN_ALMOST_SEE_IT),
  translate(T.OKAY_THIS_ONE_DETAIL_IS_BEING_STUBBORN),
  translate(T.JUST_PRETENDING_TO_BE_SLOW_NOW)
]

const PROGRESS_PHASES: IProgressPhase[] = [
  { maxProgress: 15, baseStep: 1, baseDelay: 300, speedMultiplier: 1.0 },
  { maxProgress: 44, baseStep: 1, baseDelay: 300, speedMultiplier: 0.7 },
  { maxProgress: 79, baseStep: 0.8, baseDelay: 400, speedMultiplier: 0.4 },
  { maxProgress: 99, baseStep: 0.6, baseDelay: 400, speedMultiplier: 0.2 }
]

const DEFAULT_EXPECTED_DURATION_SECONDS = 90
const DEFAULT_EXPECTED_DURATION_IN_MILLISECONDS =
  DEFAULT_EXPECTED_DURATION_SECONDS * 1000

const EXPECTED_TOTAL_PERCENTAGE = 99

// Optional multi-phase configuration. A single-phase generator (AI video/image)
// leaves these at the defaults (floor 0, cap 99) and behaves exactly as before.
// A phased generator (AI avatar) climbs to `phaseCap` then goes inert until
// `advancePhase` raises the floor/cap for the next phase; `renderPhaseDurationSeconds`
// is the phase-2 (render) duration used when advancing on `voiceover_generated`.
interface IProgressPhaseOptions {
  phaseFloor?: number
  phaseCap?: number
  renderPhaseDurationSeconds?: number
}

class GeneratorInstance {
  private progress: number
  private message: string = MESSAGE_RANGES[0].text
  private stallIndex: number = 0
  private timer: ReturnType<typeof setTimeout> | null = null
  private isActive: boolean = false
  private lastSavedDecile: number = 0
  private assetId: string
  private phaseFloor: number
  private phaseCap: number
  private renderPhaseDurationSeconds?: number

  constructor(
    private mediaItemId: string,
    initialAssetId: string | number,
    private expectedDurationInMilliseconds: number,
    initialProgress: number = 0,
    private useFakeAcceleration: boolean,
    private startTime: number = Date.now(),
    phaseOptions: IProgressPhaseOptions = {}
  ) {
    this.progress = initialProgress
    this.lastSavedDecile = Math.floor(initialProgress / 10)
    this.assetId = String(initialAssetId)
    this.phaseFloor = phaseOptions.phaseFloor ?? 0
    this.phaseCap = phaseOptions.phaseCap ?? EXPECTED_TOTAL_PERCENTAGE
    this.renderPhaseDurationSeconds = phaseOptions.renderPhaseDurationSeconds
  }

  public start(): void {
    if (this.isActive) return

    this.isActive = true
    this.publish()
    this.timer = setTimeout(() => this.tick(), TICK_INTERVAL_MS)
  }

  public stop(): void {
    this.isActive = false
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.publish()
  }

  public getProgress(): number {
    return Math.floor(this.progress)
  }

  public getMessage(): string {
    return this.message
  }

  public getAssetId(): string {
    return this.assetId
  }

  public getMediaItemId(): string {
    return this.mediaItemId
  }

  public getRenderPhaseDurationSeconds(): number | undefined {
    return this.renderPhaseDurationSeconds
  }

  // Advance to the next progress phase: raise the floor/cap the elapsed-time
  // mapping spans, reset the clock to now, and jump the bar up to the new floor
  // (monotonic — never backward). Restarts ticking if the generator had gone
  // inert at the previous phase cap. Used when a real milestone (avatar
  // `voiceover_generated`) hands off from prep to the render phase.
  public enterPhase({
    phaseFloor,
    phaseCap,
    durationSeconds
  }: {
    phaseFloor: number
    phaseCap: number
    durationSeconds: number
  }): void {
    this.phaseFloor = phaseFloor
    this.phaseCap = phaseCap
    this.expectedDurationInMilliseconds = durationSeconds * 1000
    this.startTime = Date.now()
    this.progress = Math.max(this.progress, phaseFloor)
    this.lastSavedDecile = this.currentDecile
    this.updateStatusMessage()

    if (this.isActive) {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.timer = setTimeout(() => this.tick(), TICK_INTERVAL_MS)
    }
    this.publish()
  }

  private get currentDecile(): number {
    return this.progress === EXPECTED_TOTAL_PERCENTAGE
      ? EXPECTED_TOTAL_PERCENTAGE
      : Math.floor(this.progress / 10)
  }

  public setProgress(progress: number): void {
    this.progress = progress
    this.updateStatusMessage()
    this.publish()
  }

  public updateAssetId(assetId: string | number): void {
    this.assetId = String(assetId)
  }

  private get isStallPhase(): boolean {
    return this.progress > STALL_THRESHOLD
  }
  // The final phase runs to 99 and, on completion, to 100 via complete(). A
  // non-final phase (avatar prep) tops out at a lower cap and waits inert for
  // advancePhase — so its cap must NOT trigger the 99% stall messages.
  private get isFinalPhase(): boolean {
    return this.phaseCap >= EXPECTED_TOTAL_PERCENTAGE
  }
  private get hasReachedPhaseCap(): boolean {
    return this.progress >= this.phaseCap
  }
  // Non-final phase reached its ceiling: stop advancing and wait for
  // advancePhase to raise the cap. Stays `isActive` so read APIs still report it.
  private holdAtPhaseCap(): void {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.publish()
  }
  private get currentPhase() {
    return (
      PROGRESS_PHASES.find(p => this.progress < p.maxProgress) ||
      PROGRESS_PHASES.at(-1)!
    )
  }

  private tick(): void {
    if (!this.isActive || this.progress >= LOADING_FINAL_MANDATORY_VALUE) return

    if (this.useFakeAcceleration) {
      this.tickEased()
    } else {
      this.tickLinear()
    }

    // to resume in history in other tabs or in reload case in other tabs

    const currentDecile = this.currentDecile

    if (currentDecile > this.lastSavedDecile) {
      // read current data to preserve assetId that might have been updated by socket handlers
      updateLoadingProgressDataInLocalStorage({
        id: this.mediaItemId,
        assetId: this.assetId,
        progress: this.progress
      })
      this.lastSavedDecile = currentDecile
    }
  }
  // ease in out(manual) progress
  private tickEased(): void {
    if (this.isStallPhase) {
      this.handleStallPhase()
      return
    }
    // Non-final phase reached its cap → go inert until advancePhase. (Final-phase
    // generators fall through and keep re-computing at 99, exactly as before.)
    if (!this.isFinalPhase && this.hasReachedPhaseCap) {
      this.holdAtPhaseCap()
      return
    }

    this.calculateNextEasedProgress()
    this.updateStatusMessage()
    this.publish()

    const phase = this.currentPhase
    if (this.isActive) {
      this.timer = setTimeout(() => this.tick(), phase.baseDelay)
    }
  }
  // for images we use linear progress
  private tickLinear(): void {
    if (this.isStallPhase) {
      this.handleStallPhase()
      return
    }
    if (!this.isFinalPhase && this.hasReachedPhaseCap) {
      this.holdAtPhaseCap()
      return
    }

    const elapsed = Date.now() - this.startTime
    const progressRatio = Math.min(
      elapsed / this.expectedDurationInMilliseconds,
      1.0
    )
    // Calculate target progress as a whole number, mapped into the current
    // phase's [floor, cap] band (floor 0 / cap 99 for single-phase generators).
    const phaseSpan = this.phaseCap - this.phaseFloor
    const targetProgress = Math.floor(
      this.phaseFloor + progressRatio * phaseSpan
    )

    // Only update if we've moved to a new whole number
    if (targetProgress > Math.floor(this.progress)) {
      this.progress = targetProgress
      this.updateStatusMessage()
      this.publish()
    }

    // Continue ticking until we reach the phase cap
    if (this.progress < this.phaseCap) {
      // Calculate delay based on expected duration
      // Update more frequently to catch whole number changes
      const delay = Math.max(
        this.expectedDurationInMilliseconds / TARGET_UPDATES_COUNT,
        MIN_UPDATE_DELAY_MS
      )
      if (this.isActive) {
        this.timer = setTimeout(() => this.tick(), delay)
      }
    }
  }
  // for fake acceleration case, we need to manipulate the progress to make it slower or accelerate it depending on the phase
  private calculateNextEasedProgress(): void {
    const phase = this.currentPhase
    const elapsed = Date.now() - this.startTime
    // Map elapsed time into the current phase's [floor, cap] band. For a
    // single-phase generator (floor 0 / cap 99) this is the original
    // `(elapsed / expected) * 99` schedule.
    const phaseSpan = this.phaseCap - this.phaseFloor
    const targetProgress = Math.min(
      this.phaseFloor +
        (elapsed / this.expectedDurationInMilliseconds) * phaseSpan,
      this.phaseCap
    )

    let step = phase.baseStep * phase.speedMultiplier
    if (this.progress < targetProgress) {
      // move faster if behind schedule( schedule is calc. according to the expected duration)
      const catchUpStep = Math.min(targetProgress - this.progress, step * 2)
      step = Math.max(step, catchUpStep)
    }

    this.progress = Math.min(this.progress + step, this.phaseCap)
  }

  private updateStatusMessage(): void {
    const range = MESSAGE_RANGES.find(
      range =>
        this.progress >= range.startThreshold &&
        this.progress < range.endThreshold
    )
    if (range) this.message = range.text
  }

  private handleStallPhase(): void {
    if (this.stallIndex < STALL_MESSAGES.length) {
      this.message = STALL_MESSAGES[this.stallIndex++]
      this.publish()
      if (this.isActive) {
        this.timer = setTimeout(
          () => this.tick(),
          STALL_PHASE_UPDATE_INTERVAL_MS
        )
      }
    }
  }

  private publish(): void {
    eventBus.publish(
      EVENT_TYPES.GENERATION_PROGRESS_UPDATE,
      this.mediaItemId,
      this.assetId,
      Math.floor(this.progress),
      this.message,
      this.isActive
    )
  }
}
// manager service
class GenerationProgressService {
  private activeGenerators = new Map<string, GeneratorInstance>()

  // use start only if you really started generation and need to start progress, for other cases, resume
  public start(
    mediaItemId: string,
    assetId: string | number,
    expectedDurationSeconds: number = DEFAULT_EXPECTED_DURATION_SECONDS,
    useFakeAcceleration: boolean = false,
    initialProgress: number,
    // Optional multi-phase config (AI avatar). Omitted for single-phase
    // generators (AI video/image), which then run floor 0 → cap 99 as before.
    phaseOptions: IProgressPhaseOptions = {}
  ): void {
    if (!mediaItemId || expectedDurationSeconds <= 0) {
      return
    }
    const stringifiedAssetId = String(assetId)

    const existingGenerator = this.activeGenerators.get(mediaItemId)

    // if it exists check if we need to manually update its progress
    if (existingGenerator) {
      const currentProgress = existingGenerator.getProgress()
      if (initialProgress > currentProgress) {
        existingGenerator.setProgress(initialProgress)
      }
      return
    }

    const expectedDurationInMilliseconds = expectedDurationSeconds * 1000

    // only create a new generator if it doesn't exist
    const generator = new GeneratorInstance(
      mediaItemId,
      stringifiedAssetId,
      expectedDurationInMilliseconds,
      initialProgress,
      useFakeAcceleration,
      Date.now(),
      phaseOptions
    )

    this.activeGenerators.set(mediaItemId, generator)
    // in case of reloading the page we need the finish time from ls to calc the left duration accoridng to the original finish time and not start it again
    const finishTime = Date.now() + expectedDurationInMilliseconds
    updateLoadingProgressDataInLocalStorage({
      id: mediaItemId,
      assetId: stringifiedAssetId,
      progress: initialProgress,
      finishTime,
      expectedDurationInMilliseconds,
      useFakeAcceleration,
      phaseFloor: phaseOptions.phaseFloor,
      phaseCap: phaseOptions.phaseCap,
      renderPhaseDurationSeconds: phaseOptions.renderPhaseDurationSeconds
    })

    generator.start()
  }

  // Advance a running (or resumed) generator to its next phase — e.g. the avatar
  // `voiceover_generated` milestone hands off from the prep phase to the render
  // phase. `durationSeconds`, when provided, overrides the phase-2 duration with a
  // freshly-computed estimate from real data known only at this point (the avatar
  // handler passes the render time computed from the real per-screen voiceover
  // durations). When omitted, falls back to the `renderPhaseDurationSeconds`
  // captured at start() (persisted, so it survives reload). No-op if no generator
  // matches.
  public advancePhase({
    mediaItemId,
    assetId,
    phaseFloor,
    phaseCap,
    durationSeconds
  }: {
    mediaItemId?: string
    assetId?: string | number
    phaseFloor: number
    phaseCap: number
    durationSeconds?: number
  }): void {
    const generatorId = mediaItemId ?? this.getGeneratorIdPerAssetId(assetId)
    if (!generatorId) return
    const generator = this.activeGenerators.get(generatorId)
    if (!generator) return

    const resolvedDurationSeconds =
      durationSeconds && durationSeconds > 0
        ? durationSeconds
        : (generator.getRenderPhaseDurationSeconds() ??
          DEFAULT_EXPECTED_DURATION_SECONDS)

    generator.enterPhase({
      phaseFloor,
      phaseCap,
      durationSeconds: resolvedDurationSeconds
    })

    // Persist the resolved (possibly overridden) duration so a reload mid-render
    // resumes on the same schedule.
    const expectedDurationInMilliseconds = resolvedDurationSeconds * 1000
    updateLoadingProgressDataInLocalStorage({
      id: generatorId,
      progress: generator.getProgress(),
      phaseFloor,
      phaseCap,
      expectedDurationInMilliseconds,
      finishTime: Date.now() + expectedDurationInMilliseconds
    })
  }

  // used in reloading or opening parallel tab
  public resume(mediaItemId: string, initialAssetId?: string | number): void {
    const doesGeneratorExist = this.activeGenerators.has(mediaItemId)
    if (doesGeneratorExist) return

    const savedData = this.getValidatedSavedData(mediaItemId, initialAssetId)
    const {
      progress,
      finishTime,
      assetId,
      expectedDurationInMilliseconds,
      useFakeAcceleration,
      phaseFloor,
      phaseCap,
      renderPhaseDurationSeconds
    } = savedData
    const duration =
      expectedDurationInMilliseconds ??
      DEFAULT_EXPECTED_DURATION_IN_MILLISECONDS
    const resolvedCap = phaseCap ?? EXPECTED_TOTAL_PERCENTAGE
    const resolvedFloor = phaseFloor ?? 0

    const now = Date.now()
    let originalStartTime: number

    if (finishTime) {
      // if finishTime exists, calculate start time from it
      // ensure start time isn't in the future
      originalStartTime = Math.min(finishTime - duration, now)
    } else {
      // if finishTime is missing, derive start time from current progress within
      // the phase band so the schedule resumes at the right point.
      const phaseSpan = resolvedCap - resolvedFloor || EXPECTED_TOTAL_PERCENTAGE
      const elapsedTime = ((progress - resolvedFloor) / phaseSpan) * duration
      originalStartTime = now - Math.max(elapsedTime, 0)
    }

    const generator = new GeneratorInstance(
      mediaItemId,
      String(assetId),
      duration,
      Math.min(progress, resolvedCap),
      useFakeAcceleration ?? false,
      originalStartTime,
      {
        phaseFloor: resolvedFloor,
        phaseCap: resolvedCap,
        renderPhaseDurationSeconds
      }
    )

    this.activeGenerators.set(mediaItemId, generator)
    generator.start()
  }

  private getValidatedSavedData(
    mediaItemId: string,
    assetId?: string | number
  ): ILoadingProgressData {
    const defaultData = {
      id: mediaItemId,
      assetId: assetId ? String(assetId) : mediaItemId,
      progress: 0,
      finishTime: undefined,
      expectedDurationInMilliseconds: DEFAULT_EXPECTED_DURATION_IN_MILLISECONDS,
      useFakeAcceleration: false,
      phaseFloor: undefined,
      phaseCap: undefined,
      renderPhaseDurationSeconds: undefined
    }
    const savedData = getLoadingProgressFromLocalStorage(mediaItemId)
    if (!(savedData instanceof Object)) {
      return defaultData
    }

    const validateData = <K extends keyof ILoadingProgressData>(
      key: K
    ): ILoadingProgressData[K] => savedData[key] ?? defaultData[key]

    return {
      id: validateData('id'),
      assetId: validateData('assetId'),
      progress: validateData('progress'),
      finishTime: validateData('finishTime'),
      expectedDurationInMilliseconds: validateData(
        'expectedDurationInMilliseconds'
      ),
      useFakeAcceleration: validateData('useFakeAcceleration'),
      phaseFloor: validateData('phaseFloor'),
      phaseCap: validateData('phaseCap'),
      renderPhaseDurationSeconds: validateData('renderPhaseDurationSeconds')
    }
  }

  public getGeneratorIdPerAssetId(
    assetId?: string | number
  ): string | undefined {
    if (!assetId) return undefined

    const assetIdStr = String(assetId)

    const generators = Array.from(this.activeGenerators.values())
    const generator = generators.find(
      generator => generator.getAssetId() === assetIdStr
    )
    if (generator) {
      return generator.getMediaItemId()
    }

    const generationProgressData =
      getLoadingProgressFromLocalStorage(assetIdStr)
    return generationProgressData?.id ?? undefined
  }

  public stop({
    mediaItemId,
    assetId,
    shouldRemoveFromStorage = false
  }: {
    mediaItemId?: string
    assetId?: string | number
    shouldRemoveFromStorage: boolean
  }): void {
    const generatorId = mediaItemId ?? this.getGeneratorIdPerAssetId(assetId)

    if (!generatorId) return
    const generator = this.activeGenerators.get(generatorId)
    if (generator) {
      generator.stop()
      if (this.activeGenerators.has(generatorId)) {
        this.activeGenerators.delete(generatorId)
      }
      if (shouldRemoveFromStorage) {
        removeLoadingProgressFromLocalStorage(generatorId)
      }
    }
  }

  public complete({
    mediaItemId,
    assetId
  }: {
    mediaItemId?: string
    assetId?: string | number
  }): void {
    const generatorId = mediaItemId ?? this.getGeneratorIdPerAssetId(assetId)
    if (!generatorId) return
    const generator = this.activeGenerators.get(generatorId)
    if (generator) {
      generator.setProgress(LOADING_FINAL_MANDATORY_VALUE)
      // delay so user sees 100%
      setTimeout(
        () =>
          this.stop({ mediaItemId, assetId, shouldRemoveFromStorage: true }),
        COMPLETION_DELAY_MS
      )
    }
  }

  public getProgress(mediaItemId: string): number | null {
    const generator = this.activeGenerators.get(mediaItemId)
    return generator ? generator.getProgress() : null
  }

  public getMessage(mediaItemId: string): string | null {
    const generator = this.activeGenerators.get(mediaItemId)
    return generator ? generator.getMessage() : null
  }

  public isActive(mediaItemId: string): boolean {
    return this.activeGenerators.has(mediaItemId)
  }

  public updateAssetId(mediaItemId: string, assetId: string | number): void {
    const generator = this.activeGenerators.get(mediaItemId)
    if (generator) {
      generator.updateAssetId(assetId)
      updateLoadingProgressDataInLocalStorage({
        id: mediaItemId,
        assetId
      })
    }
  }

  public stopAll(): void {
    this.activeGenerators.forEach(generator => generator.stop())
    this.activeGenerators.clear()
  }
}

export const generationProgressService = new GenerationProgressService()
